<template>
  <accordion-panel
    id="alarm-level-panel"
    :collapsed.sync="collapsedProxy"
    @update:collapsed="onCollapsedChange"
  >
    <template #title>
      <label
        class="mb-0"
      >
        {{ $t('engineering.chromatography.tab-name.alarm-level-parameters') }}
      </label>
    </template>
    <b-row
      v-if="showData"
      class="mt-4 mx-0 mb-4"
    >
      <b-col cols="12" xl="6">
        <b-card
          class="param__card align-top w-100"
        >
          <alarm-level-params-tab
            :items="gasesItems"
            :fields="paramsTabTableGasFields"
            :card-title="gasCardTitle"
            />
        </b-card>
      </b-col>
      <b-col cols="12" xl="6">
        <b-card
          class="param__card align-top w-100"
        >
          <alarm-level-params-tab
            :items="rateItems"
            :fields="paramsTabTableRatesFields"
            :card-title="rateCardTitle"
            />
        </b-card>
      </b-col>
    </b-row>
    <s-empty-list
      v-else
      :loading="loading"
    />
  </accordion-panel>
</template>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.accordion-panel
  box-shadow: none
  ::v-deep &__body
    border: none !important

.param
  &__card
    +media-max-sm
      width: 100%
    +media-min-md
      display: inline-block
    ::v-deep.card-body
      background-color: var(--color-table-body-accent-bg)
</style>

<script>
import LoadingMixin from '@/services/loadingMixin';
import AccordionPanel from '@/components/AccordionPanel';
import IndividualAnalysisService from '@/domain/services/installation/engineering/chromatography/individualAnalysis';
import AlarmLevelParamsTab from './AlarmLevelParamsTab';

export default {
  props: {
    assetModuleId: String,
    expanded: Boolean,
    sampleId: String,
  },
  components: {
    AccordionPanel,
    AlarmLevelParamsTab,
  },
  mixins: [
    LoadingMixin,
  ],
  data: () => ({
    alarmLevels: null,
  }),
  computed: {
    collapsedProxy: {
      get() {
        return !this.expanded;
      },
      set(value) {
        this.$emit('update:expanded', !value);
      },
    },
    showData() {
      return !this.loading && !!this.alarmLevels;
    },
    gasCardTitle() {
      return this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gases');
    },
    rateCardTitle() {
      return this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.rates');
    },
    gasesItems() {
      return this.alarmLevels.gases.map(alarm => this.paramsTabTableItems(alarm));
    },
    rateItems() {
      return this.alarmLevels.rates.map(alarm => this.paramsTabTableItems(alarm));
    },
    paramsTabTableGasFields() {
      return [
        {
          key: 'gas',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas'),
        },
        {
          key: 'value',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas-fields.sample-value'),
        },
        {
          key: 'highLimit',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas-fields.high-value'),
        },
        {
          key: 'veryHighLimit',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas-fields.very-high-value'),
        },
      ];
    },
    paramsTabTableRatesFields() {
      return [
        {
          key: 'gas',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.gas'),
        },
        {
          key: 'value',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.rates-fields.sample-value'),
        },
        {
          key: 'highLimit',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.rates-fields.high-value'),
        },
        {
          key: 'veryHighLimit',
          label: this.$t('engineering.chromatography.methods-and-diagnostics.alarm-levels.rates-fields.very-high-value'),
        },
      ];
    },
  },
  methods: {
    formatTableItemValue(value) {
      return value === undefined ? '--' : value;
    },
    paramsTabTableItems(alarm) {
      return {
        ...alarm,
        gas: this.$t(`engineering.chromatography.chemical-elements.${alarm.gas.toLowerCase()}`),
        value: this.formatTableItemValue(alarm.value),
        highLimit: this.formatTableItemValue(alarm.highLimit),
        veryHighLimit: this.formatTableItemValue(alarm.veryHighLimit),
      };
    },
    onCollapsedChange(collapsed) {
      if (!collapsed && this.alarmLevels == null) {
        this.loadData();
      }
    },
    loadData() {
      this.startLoading();
      IndividualAnalysisService
        .getAlarmLevelParams(this.assetModuleId, this.sampleId)
        .then((response) => {
          this.alarmLevels = response;
        })
        .finally(() => this.endLoading());
    },
  },
  created() {
    if (this.expanded) {
      this.loadData();
    }
  },
};
</script>
